import { Box, Button, Container, Divider, Stack, SvgIcon, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

function onlyDigits(value = '') {
  return String(value || '').replace(/\D/g, '');
}

function buildWhatsappUrl(number) {
  const phone = onlyDigits(number);
  if (!phone) return '';

  const message = encodeURIComponent(
    'Hello Fresh Exports, I would like information about your flower catalog.'
  );

  return `https://wa.me/${phone}?text=${message}`;
}

export default function PublicFooter({ config = {} }) {
  const year = new Date().getFullYear();

  const siteTitle = config.site_title || 'Fresh Exports';

  const subtitle =
    config.footer_descripcion ||
    config.site_subtitle ||
    'Premium Ecuadorian flowers selected with care for international markets.';

  const phone = config.telefono_publico || '';
  const whatsapp = config.whatsapp_publico || phone || '';
  const email = config.correo_publico || '';
  const address = config.direccion_publica || '';

  const footerColumnTitle = config.footer_columna_titulo || 'Export Quality';

  const footerColumnText =
    config.footer_columna_texto ||
    'Fresh flowers, careful selection, elegant presentation and commercial support for international buyers.';

  const footerButtonText = config.footer_boton_texto || 'View catalog';
  const footerButtonLink = config.footer_boton_link || '/catalogo';

  const footerFinalText = config.footer_frase_final || 'Ecuadorian flowers for global markets.';

  const whatsappButtonText = config.whatsapp_boton_texto || 'WhatsApp';

  const whatsappUrl = buildWhatsappUrl(whatsapp);

  const socialLinks = [
    { label: 'Instagram', url: config.instagram_url },
    { label: 'Facebook', url: config.facebook_url },
    { label: 'TikTok', url: config.tiktok_url }
  ].filter((item) => item.url);

  const isFooterButtonExternal = /^https?:\/\//i.test(footerButtonLink);

  const footerButtonProps = isFooterButtonExternal
    ? {
        component: 'a',
        href: footerButtonLink,
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    : {
        component: RouterLink,
        to: footerButtonLink
      };

  return (
    <>
      <Box
        component="footer"
        sx={{
          bgcolor: '#07110c',
          color: '#fff',
          pt: { xs: 7, md: 9 },
          pb: 3
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1.5fr .9fr 1.1fr 1.1fr'
              },
              gap: { xs: 4, md: 5 }
            }}
          >
            <Stack spacing={2.2}>
              <Typography
                sx={{
                  fontFamily: "'VivaldiCustom', serif",
                  fontStyle: 'italic',
                  fontSize: { xs: 38, md: 46 },
                  lineHeight: 1
                }}
              >
                {siteTitle}
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(255,255,255,.68)',
                  lineHeight: 1.8,
                  maxWidth: 420
                }}
              >
                {subtitle}
              </Typography>

              {!!socialLinks.length && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {socialLinks.map((item) => (
                    <Button
                      key={item.label}
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,.20)',
                        borderRadius: 999,
                        px: 2,
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,.10)',
                          borderColor: 'rgba(255,255,255,.42)'
                        }
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>

            <Stack spacing={1.3}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: 14,
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                  color: '#d8b16a',
                  mb: 1
                }}
              >
                Navigation
              </Typography>

              {[
                { label: 'Home', to: '/' },
                { label: 'About us', to: '/#quienes-somos' },
                { label: 'Catalog', to: '/catalogo' },
                { label: 'Registration', to: '/#registro' },
                { label: 'Contact us', to: '/#contactanos' }
              ].map((item) => (
                <Typography
                  key={item.label}
                  component={RouterLink}
                  to={item.to}
                  sx={{
                    color: 'rgba(255,255,255,.72)',
                    textDecoration: 'none',
                    width: 'fit-content',
                    '&:hover': {
                      color: '#fff'
                    }
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Stack>

            <Stack spacing={1.3}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: 14,
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                  color: '#d8b16a',
                  mb: 1
                }}
              >
                Contact
              </Typography>

              {!!whatsapp && (
                <Typography sx={{ color: 'rgba(255,255,255,.72)', lineHeight: 1.7 }}>
                  WhatsApp: {whatsapp}
                </Typography>
              )}

              {!!phone && (
                <Typography sx={{ color: 'rgba(255,255,255,.72)', lineHeight: 1.7 }}>
                  Phone: {phone}
                </Typography>
              )}

              {!!email && (
                <Typography
                  component="a"
                  href={`mailto:${email}`}
                  sx={{
                    color: 'rgba(255,255,255,.72)',
                    textDecoration: 'none',
                    lineHeight: 1.7,
                    '&:hover': { color: '#fff' }
                  }}
                >
                  {email}
                </Typography>
              )}

              {!!address && (
                <Typography sx={{ color: 'rgba(255,255,255,.72)', lineHeight: 1.7 }}>
                  {address}
                </Typography>
              )}
            </Stack>

            <Stack spacing={2}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: 14,
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                  color: '#d8b16a'
                }}
              >
                {footerColumnTitle}
              </Typography>

              <Typography sx={{ color: 'rgba(255,255,255,.72)', lineHeight: 1.8 }}>
                {footerColumnText}
              </Typography>

              <Button
                {...footerButtonProps}
                variant="outlined"
                sx={{
                  width: 'fit-content',
                  borderRadius: 999,
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,.35)',
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 800,
                  '&:hover': {
                    borderColor: '#fff',
                    bgcolor: 'rgba(255,255,255,.10)'
                  }
                }}
              >
                {footerButtonText}
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,.12)', my: 4 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Typography sx={{ color: 'rgba(255,255,255,.52)', fontSize: 14 }}>
              © {year} {siteTitle}. All rights reserved.
            </Typography>

            <Typography sx={{ color: 'rgba(255,255,255,.52)', fontSize: 14 }}>
              {footerFinalText}
            </Typography>
          </Stack>
        </Container>
      </Box>

      {!!whatsappUrl && (
        <Button
          component="a"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={whatsappButtonText || 'Chat on WhatsApp'}
          title="Chat on WhatsApp"
          sx={{
            position: 'fixed',
            right: { xs: 16, md: 24 },
            bottom: { xs: 16, md: 24 },
            zIndex: 1200,
            borderRadius: '50%',
            width: { xs: 56, md: 60 },
            height: { xs: 56, md: 60 },
            minWidth: 0,
            p: 0,
            bgcolor: '#183f2a',
            color: '#fff',
            fontWeight: 900,
            textTransform: 'none',
            boxShadow: '0 18px 45px rgba(0,0,0,.25)',
            '&:hover': {
              bgcolor: '#102d1d',
              transform: 'translateY(-2px)'
            },
            '&:focus-visible': { outline: '3px solid #d8b16a', outlineOffset: 4 },
            transition: 'all .25s ease'
          }}
        >
          <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 32 }} aria-hidden="true">
            <path d="M20.5 11.6a8.5 8.5 0 0 1-12.7 7.4L3 20.5l1.4-4.7A8.5 8.5 0 1 1 20.5 11.6Z"
              fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.91-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.23 1.36.2 1.88.12.58-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.28-.2-.57-.35Z"
              transform="translate(2.1 2.1) scale(.8)" />
          </SvgIcon>
        </Button>
      )}
    </>
  );
}
